import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '../lib/services/api'
import { PRACTICE_CASE_ENDPOINTS } from './endpoints'

// Practice case types
export interface PracticeCase {
  id: string
  title: string
  location: string
  status: 'queued' | 'generating' | 'ready' | 'error' | 'deleted'
  errorMessage: string | null
  fileMetadata: Array<{
    filename: string
    originalFilename: string
    fileType: string
    fileSize: number
    uploadedAt: string
  }>
  createdAt: string | null
  updatedAt: string | null
  createdBy: {
    id: string
    name: string
  } | null
  articleCount: number
  generationStartedAt: string | null
  generationCompletedAt: string | null
  generationModel: string | null
  generationTokens: number | null
}

export interface PracticeArticle {
  id: string
  articleJson: Record<string, unknown>
  publishedAt: string
  syntheticFlag: boolean
  createdAt: string
}

export interface PracticeCaseListResponse {
  data: PracticeCase[]
}

export interface PracticeCaseResponse {
  data: PracticeCase
}

export interface PracticeArticleListResponse {
  data: PracticeArticle[]
}

export interface CreatePracticeCaseData {
  title: string
  location: string
  files: File[]
  numArticles?: number
}

export interface UpdatePracticeCaseData {
  title?: string
  location?: string
}

// Hook to fetch all practice cases (admin only)
export const usePracticeCases = (includeDeleted = false) => {
  return useQuery({
    queryKey: ['practice-cases', includeDeleted],
    queryFn: async (): Promise<PracticeCaseListResponse> => {
      return apiService.get(PRACTICE_CASE_ENDPOINTS.LIST)
    },
  })
}

// Hook to fetch single practice case by ID (admin only)
export const usePracticeCase = (caseId: string | null) => {
  return useQuery({
    queryKey: ['practice-case', caseId],
    queryFn: async (): Promise<PracticeCaseResponse> => {
      if (!caseId) throw new Error('Case ID is required')
      return apiService.get(PRACTICE_CASE_ENDPOINTS.CASE_BY_ID(caseId))
    },
    enabled: !!caseId,
  })
}

// Hook to fetch articles for a practice case (admin only)
export const usePracticeCaseArticles = (caseId: string | null) => {
  return useQuery({
    queryKey: ['practice-case-articles', caseId],
    queryFn: async (): Promise<PracticeArticleListResponse> => {
      if (!caseId) throw new Error('Case ID is required')
      return apiService.get(PRACTICE_CASE_ENDPOINTS.ARTICLES(caseId))
    },
    enabled: !!caseId,
  })
}

// Hook to fetch ready practice cases (all authenticated users)
export const useReadyPracticeCases = () => {
  return useQuery({
    queryKey: ['ready-practice-cases'],
    queryFn: async (): Promise<PracticeCaseListResponse> => {
      return apiService.get(PRACTICE_CASE_ENDPOINTS.PUBLIC_LIST)
    },
  })
}

// Hook to create practice case (admin only)
export const useCreatePracticeCase = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreatePracticeCaseData): Promise<PracticeCaseResponse> => {
      const formData = new FormData()
      formData.append('title', data.title)
      formData.append('location', data.location)
      if (data.numArticles !== undefined) {
        formData.append('num_articles', data.numArticles.toString())
      }
      data.files.forEach(file => {
        formData.append('files', file)
      })

      return apiService.post(PRACTICE_CASE_ENDPOINTS.CREATE, formData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-cases'] })
    },
  })
}

// Hook to update practice case (admin only)
export const useUpdatePracticeCase = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      caseId,
      data,
    }: {
      caseId: string
      data: UpdatePracticeCaseData
    }): Promise<PracticeCaseResponse> => {
      return apiService.patch(PRACTICE_CASE_ENDPOINTS.UPDATE(caseId), data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-cases'] })
      queryClient.invalidateQueries({ queryKey: ['practice-case'] })
    },
  })
}

// Hook to delete practice case (admin only)
export const useDeletePracticeCase = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (caseId: string): Promise<{ data: { id: string; deleted: boolean } }> => {
      return apiService.delete(PRACTICE_CASE_ENDPOINTS.DELETE(caseId))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-cases'] })
      queryClient.invalidateQueries({ queryKey: ['ready-practice-cases'] })
    },
  })
}
